import React, { FunctionComponent, useEffect, useState } from 'react'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import type { DevUtils } from '@dustin-web/microsite-components/src/dev/dev-admin'
import EolIe from '@dustin-web/microsite-components/src/eol-ie'
import {
  useBot,
  useResponsive,
  useSiteContext,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import { BaseLayoutInputPageConfig, constants } from '@dustin-web/microsite-config'
import classnames from 'classnames'
import dynamic from 'next/dynamic'
import { RenderScripts } from './3rd-party-scripts'
import BreakpointIndicator from './breakpoint-indicator'
import Favicon from './favicon'
import Header from './header'
import { HeaderContextProvider } from './header/header-context'
import LinkedData from './linked-data-client'
import ScrollToTopButton from './scroll-to-top-btn'
import ThemeColor from './theme-color'
import { usePathname } from 'next/navigation'
import UspBanner from './header/usp-banner'
import { blackWeekUrlExceptions } from '@dustin-web/microsite-data/src/utils'
import { AccessibilitySkipLink } from '@dustin-web/microsite-components/src/accessibility-skip-link'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const Footer = dynamic(() => import('./footer'))
const CampaignBanner = dynamic(
  () => import('@dustin-web/content-components/src/campaign-banner/campaign-banner')
)
const NotificationBannerContainer = dynamic(
  () =>
    import(
      '@dustin-web/content-components/src/notification-banner-container/notification-banner-container'
    )
)
const BlackFridaySignup = dynamic(
  () => import('@dustin-web/microsite-components/src/black-friday-signup')
)
const CookieBanner = dynamic(() => import('@dustin-web/microsite-components/src/cookie-banner'))
const CartNotification = dynamic(() => import('./header/cart/cart-notification'))

const MicrositeContextDevAdmin = dynamic(
  () => import('@dustin-web/microsite-components/src/dev/dev-admin')
)

type Props = {
  devUtils: DevUtils
  page?: BaseLayoutInputPageConfig
  children: React.ReactNode
  themeClass?: string
}

const BaseLayout: FunctionComponent<Props> = ({ page, devUtils, children, themeClass }) => {
  const { isSmallScreen } = useResponsive()
  const isBot = useBot()
  const pathname = usePathname() || ''
  const isStartPage = pathname.split('?')[0] === '/' ? true : false
  const isShoppingCart = pathname.split('?')[0] === '/shoppingcart' ? true : false

  const { enabled: isBlackFridaySignupEnabled } = useFeatureFlag('black-friday-signup')
  const { enabled: isBreakpointIndicatorEnabled } = useFeatureFlag('breakpoint-indicator', true)
  const { enabled: isDevAdminEnabled } = useFeatureFlag('dev-admin-ui', true)

  const { userData } = useAuth()

  const { translate } = useTranslations()
  const { cookies, setCookie } = useCookieState()
  const isAdmin = userData?.allowImpersonateContact ?? false
  const [isIE11, setIsIE11] = useState<boolean>(false)

  const urlExceptions = blackWeekUrlExceptions
  const currentPathIsException = urlExceptions.some((phrase: string) => pathname.includes(phrase))

  const changeUsp = () => {
    const hideUspPaths = ['oplossingen']
    const path = pathname.split('/')[1]
    const hideUsp = hideUspPaths.some(hideUspPath => hideUspPath === path)
    const usp = document.getElementById('usp-bar')
    // Hide usp-bar on certain paths
    if (hideUsp) {
      usp?.classList.add('d-none')
    }
    // Remove border-bottom from usp if startpage has a takeover-banner
    else if (isStartPage) {
      const hasTakover = document.getElementsByClassName('c-startpage-takeover-wrapper')[0]
      if (hasTakover && usp) {
        usp.getElementsByClassName('c-box--section')[0].classList.remove('u-bb')
      }
    }
  }

  useEffect(() => {
    changeUsp()
    if ('ActiveXObject' in window) {
      alert(isIE11)

      setTimeout(function () {
        setIsIE11(true)
      }, 1)
    }
  }, [pathname])

  useEffect(() => {
    if (isAdmin && !cookies.userSettings.showMargins) {
      setTimeout(() => {
        const result = confirm(translate('Show_Margins'))
        setCookie(constants.cookies.userSettings.showMargins, result)
      }, 500)
    }
  }, [isAdmin, cookies.userSettings.showMargins])

  const { siteUrl, isProxied, isDevelopment } = useSiteContext()
  const isStandaloneProxied = siteUrl.includes('dustin.services') && isProxied

  const shouldShowDevAdmin = isDevelopment || !isProxied || isStandaloneProxied || isDevAdminEnabled

  const [showDevAdmin, setShowDevAdmin] = useState(false)
  useEffect(() => {
    if (shouldShowDevAdmin) {
      setShowDevAdmin(true)
    }
  })

  const pageConfig = {
    backgroundColor: 'gray',
    ...page,
  }

  return (
    <>
      <RenderScripts />
      <ThemeColor />
      <Favicon />
      <LinkedData />
      <div className={themeClass}>
        <div
          className={classnames('business', {
            startpage: isStartPage,
          })}>
          {isIE11 === true ? (
            <div
              className="t-light-blue d-flex justify-content-center u-pb-80"
              style={{ height: '100vh', paddingTop: '80px' }}>
              <EolIe />
            </div>
          ) : (
            <>
              <AccessibilitySkipLink
                navigationId="main-content"
                linkText={translate('Skip_Accessibility_Link')}>
                <UspBanner />
                <ErrorBoundary>
                  <HeaderContextProvider>
                    <Header />
                  </HeaderContextProvider>
                </ErrorBoundary>
                <CampaignBanner />
                <NotificationBannerContainer />
                <main
                  id="main-content"
                  className={classnames('c-page', {
                    'c-page--white': pageConfig.backgroundColor === 'white',
                  })}>
                  {children}
                </main>
              </AccessibilitySkipLink>
              <ErrorBoundary>{!isBot && <CookieBanner />}</ErrorBoundary>
              <AccessibilitySkipLink
                overflowHidden
                navigationId="main-content"
                linkText={translate('Skip_Accessibility_Link')}>
                <ErrorBoundary>
                  <Footer />
                </ErrorBoundary>
              </AccessibilitySkipLink>
              <ErrorBoundary>
                {!isShoppingCart && !isSmallScreen && <CartNotification />}
              </ErrorBoundary>
              <ErrorBoundary>
                <ScrollToTopButton />
              </ErrorBoundary>
            </>
          )}
          <ErrorBoundary>{isBreakpointIndicatorEnabled && <BreakpointIndicator />}</ErrorBoundary>
          <ErrorBoundary>
            {showDevAdmin && <MicrositeContextDevAdmin devUtils={devUtils} />}
          </ErrorBoundary>
          {isBlackFridaySignupEnabled && !currentPathIsException && <BlackFridaySignup />}
        </div>
      </div>
    </>
  )
}

export default BaseLayout
